import {
  Avatar,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  IconButton,
  Stack,
  TableCell,
  TableRow,
  Tooltip,
  Typography,
} from '@mui/material';
import { RotateCcwIcon, SquarePenIcon, Trash2Icon, XIcon } from 'lucide-react';
import { useState } from 'react';
import { Team } from '../../types/Team/Team.type';
import { User } from '../../types/User/User.type.ts';
import { UserStatus } from '../../types/enums/UserStatus.enum';
import { getUserStatusStyle } from '../../utils/userStatusStyle';
import UserEditModal from './UserEditModal';
import { UserUpdate } from '../../types/User/UserUpdate.ts';

interface UserRowProps {
  user: User;
  teams: Team[];
  isLoading: boolean;
  onDeactivate: (email: string) => void;
  onCancelInvite: (email: string) => void;
  onResendInvite: (email: string) => void;
  onUpdate: (userId: number, payload: UserUpdate) => void;
}

function UserRow({ user, teams, isLoading, onDeactivate, onCancelInvite, onResendInvite, onUpdate }: UserRowProps) {
  const statusStyle = getUserStatusStyle(user.user_status.id);
  const isPending = user.user_status.id === UserStatus.PENDING;
  const isActive = user.user_status.id === UserStatus.ACTIVE;

  const [editOpen, setEditOpen] = useState(false);
  const [deactivateOpen, setDeactivateOpen] = useState(false);
  const [cancelOpen, setCancelOpen] = useState(false);
  const [resendOpen, setResendOpen] = useState(false);

  const fullName = `${user.first_name ?? ''} ${user.last_name ?? ''}`.trim();

  const handleEditSubmit = (payload: UserUpdate) => {
    onUpdate(user.id, payload);
    setEditOpen(false);
  };

  const handleDeactivateConfirm = () => {
    onDeactivate(user.email);
    setDeactivateOpen(false);
  };

  const handleCancelConfirm = () => {
    onCancelInvite(user.email);
    setCancelOpen(false);
  };

  const handleResendConfirm = () => {
    onResendInvite(user.email);
    setResendOpen(false);
  };

  return (
    <TableRow hover>
      <TableCell>
        <Stack
          direction='row'
          spacing={2}
          sx={{
            alignItems: 'center',
          }}
        >
          <Avatar src={user.profile_picture_url || ''}>
            {user.first_name?.charAt(0) || user.email?.charAt(0).toUpperCase()}
          </Avatar>
          <Stack>
            {fullName && (
              <Typography
                variant='subtitle2'
                sx={{
                  fontWeight: 'medium',
                }}
              >
                {fullName}
              </Typography>
            )}
            <Typography
              variant='caption'
              sx={{
                color: 'text.secondary',
              }}
            >
              {user.email}
            </Typography>
          </Stack>
        </Stack>
      </TableCell>
      <TableCell>
        <Typography variant='body2'>{user.team?.name ?? '—'}</Typography>
      </TableCell>
      <TableCell>
        <Typography variant='body2'>{user.role.name}</Typography>
      </TableCell>
      <TableCell>
        <Box
          component='span'
          sx={{
            display: 'inline-flex',
            alignItems: 'center',
            paddingX: 1,
            paddingY: '2px',
            borderRadius: '6px',
            backgroundColor: statusStyle.backgroundColor,
            color: statusStyle.color,
            fontSize: '12px',
            fontWeight: 600,
          }}
        >
          {statusStyle.name}
        </Box>
      </TableCell>
      <TableCell align='right'>
        <Stack
          direction='row'
          spacing={1}
          sx={{
            justifyContent: 'flex-end',
          }}
        >
          {!isPending && (
            <Tooltip title='Edit user'>
              <span>
                <IconButton aria-label='Edit user' onClick={() => setEditOpen(true)} disabled={isLoading}>
                  <SquarePenIcon size={20} />
                </IconButton>
              </span>
            </Tooltip>
          )}
          {!isActive && (
            <Tooltip title='Resend invite'>
              <span>
                <IconButton aria-label='Resend invite' onClick={() => setResendOpen(true)} disabled={isLoading}>
                  <RotateCcwIcon size={20} />
                </IconButton>
              </span>
            </Tooltip>
          )}
          {isPending && (
            <Tooltip title='Cancel invite'>
              <span>
                <IconButton aria-label='Cancel invite' onClick={() => setCancelOpen(true)} disabled={isLoading}>
                  <XIcon size={20} />
                </IconButton>
              </span>
            </Tooltip>
          )}
          {isActive && (
            <Tooltip title='Deactivate user'>
              <span>
                <IconButton aria-label='Deactivate user' onClick={() => setDeactivateOpen(true)} disabled={isLoading}>
                  <Trash2Icon size={20} />
                </IconButton>
              </span>
            </Tooltip>
          )}
        </Stack>
      </TableCell>
      <UserEditModal
        open={editOpen}
        onClose={() => setEditOpen(false)}
        user={user}
        teams={teams}
        loading={isLoading}
        onUpdate={handleEditSubmit}
      />
      {/* Deactivate (active user) */}
      <Dialog maxWidth='xs' fullWidth open={deactivateOpen} onClose={() => setDeactivateOpen(false)}>
        <DialogTitle sx={{ fontSize: '16px' }}>Deactivate User</DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ fontSize: '14px' }}>
            {`Are you sure you want to deactivate ${fullName || user.email}? This user will lose access to the platform.`}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeactivateOpen(false)}>Cancel</Button>
          <Button onClick={handleDeactivateConfirm} color='error' variant='contained' disableElevation>
            Deactivate
          </Button>
        </DialogActions>
      </Dialog>
      {/* Cancel invite (pending user) */}
      <Dialog maxWidth='xs' fullWidth open={cancelOpen} onClose={() => setCancelOpen(false)}>
        <DialogTitle sx={{ fontSize: '16px' }}>Cancel Invitation</DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ fontSize: '14px' }}>
            Are you sure you want to cancel the invitation for {user.email}?
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCancelOpen(false)}>Close</Button>
          <Button onClick={handleCancelConfirm} color='error' variant='contained' disableElevation>
            Confirm
          </Button>
        </DialogActions>
      </Dialog>
      {/* Resend invite (pending user) */}
      <Dialog maxWidth='xs' fullWidth open={resendOpen} onClose={() => setResendOpen(false)}>
        <DialogTitle sx={{ fontSize: '16px' }}>Resend Invitation</DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ fontSize: '14px' }}>
            This will send a new invitation email to {user.email}. Do you want to proceed?
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setResendOpen(false)}>Close</Button>
          <Button onClick={handleResendConfirm} variant='contained' disableElevation>
            Resend
          </Button>
        </DialogActions>
      </Dialog>
    </TableRow>
  );
}

export default UserRow;
